import { Link, useParams } from "react-router-dom";
import BrandMark from "@/components/BrandMark";
import { FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import SiteFooter from "@/components/SiteFooter";


/**
 * Picker for one document group. Generic on purpose: the group comes from the route, so a group
 * you create templates under gets a working page without a code change. `/customer/affidavits`
 * keeps working as the affidavit-shaped entry point.
 */
export default function AffidavitsIndex() {
  const { user } = useAuth();
  const { group: routeGroup } = useParams();
  const group = routeGroup || "affidavit";
  const [meta, setMeta] = useState(null);
  // Driven by the template catalogue, not a list in this file. The affidavit types used to be
  // hardcoded here, so a template created in admin was unreachable by customers and a template
  // deleted there still showed. `enabled: false` now genuinely hides one.
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    api.get("/templates", { params: { group } })
      .then(r => setItems((r.data || []).filter(t => t.enabled !== false)))
      .catch(() => setItems([]));
    api.get("/template-groups")
      .then(r => setMeta((r.data || []).find(g => g.key === group) || null))
      .catch(() => {});
  }, [group]);
  const label = meta?.label || "Documents";
  const isCustomer = user?.role === "customer";
  // The wizard is customer-only either way; a signed-out visitor hits Protected and is sent to login.
  const wizardBase = `/customer/wizard/${group}?type=`;
  const wrapperCls = isCustomer ? "" : "min-h-screen bg-[#080C16] text-white";
  const content = (
      // `affidavits-index` is the page's render marker — the UI smoke suite (frontend/tests/e2e) waits on
      // one stable testid per page to tell "rendered" from "blank shell".
      <div data-testid="affidavits-index" className={isCustomer ? "" : "max-w-6xl mx-auto px-6 py-16"}>
        <div className={`text-[10px] tracking-[0.32em] uppercase ${isCustomer ? "text-[#CBA96C]" : "text-[#6B7280]"}`}>{label}</div>
        <h1 className={`font-serif-display text-4xl md:text-5xl mt-2 ${isCustomer ? "text-white" : "text-white"}`}>Choose a document.</h1>
        <p className={`mt-4 max-w-2xl ${isCustomer ? "text-[#A0AABF]" : "text-[#A0AABF]"}`}>Each one opens a guided questionnaire with a live preview and advocate review.</p>

        <div className="mt-12">
          {items === null && <div className={isCustomer ? "text-[#A0AABF]" : "text-[#6B7280]"}>Loading documents…</div>}
          {items?.length === 0 && (
            <div className={isCustomer ? "text-[#A0AABF]" : "text-[#6B7280]"}>
              No documents are available in this group right now.
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(items || []).map((it) => (
              <Link key={it.key} data-testid={`affidavit-card-${it.key}`} to={`${wizardBase}${it.key}`}
                className={`group flex items-start gap-4 rounded-2xl p-5 border transition-colors ${
                  isCustomer
                    ? "bg-[#111623] border-white/10 hover:border-[#CBA96C]/40"
                    : "bg-[#111623] border-white/10 hover:border-[#CBA96C]/40"
                }`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isCustomer ? "bg-white/5" : "bg-white/5"}`}>
                  <FileText className="w-5 h-5 text-[#CBA96C]" strokeWidth={1.5}/>
                </div>
                <div className="min-w-0">
                  <div className={`font-medium ${isCustomer ? "text-white" : "text-white"}`}>{it.title}</div>
                  <div className={`text-xs mt-1 ${isCustomer ? "text-[#A0AABF]" : "text-[#6B7280]"}`}>Start →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
  );
  if (isCustomer) return content;
  // Signed-out visitors get the same catalogue with the public header around it. This branch used
  // to duplicate the entire grid — two copies of the same list that had to be edited in step.
  return (
    <div className={wrapperCls}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#080C16]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-serif-display text-lg tracking-tight text-white">
            <BrandMark /> DharmaTN
          </Link>
          <Link to="/login" className="text-sm text-[#A0AABF] hover:text-white transition-colors">Sign In</Link>
        </div>
      </header>
      {content}
      {/* Only on the standalone public page — inside the customer portal this renders within
          CustomerLayout, which carries the footer already. */}
      {!isCustomer && <SiteFooter/>}
    </div>
  );
}
